import { useCallback, useMemo } from 'react';

import { Subject, Years } from 'global/constants';
import { OutcomeOption } from 'lib/form/tag-autocomplete/outcomes/tag-autocomplete-outcomes.constants';
import TagAutocompleteOutcomes from 'lib/form/tag-autocomplete/outcomes/tag-autocomplete-outcomes';
import { flattenObjectToArrayWithKey } from 'approot/shared/outcomes/outcome.utils';
import sortPerformanceExpectationDimensions from 'approot/shared/outcomes/sort-performance-expectations-dimensions';
import { Box } from 'approot/shared/design-system/box/box.style';
import { LinkButton } from 'approot/shared/styles/buttons/link-button.style';
import { PrimaryButton } from 'approot/shared/styles/buttons/primary-button';
import { CheckableMulti } from 'approot/shared/styles/buttons/checkable-input';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import {
  CER_TYPE_OPTIONS,
  PerformanceExpectationGroup,
  QUESTION_TYPES,
} from '../activity-outcomes.constants';
import { usePerformanceExpectationStandards } from './use-performance-expectation-standards';
import ButtonDismiss from 'approot/shared/design-system/button-dismiss';
import Bouncer from 'approot/shared/bouncer/bouncer';

type PerformanceExpectationsEditProps = {
  subject: Subject;
  years: Years[];
  initialGroup?: PerformanceExpectationGroup;
  isSaving?: boolean;
  /** Unique id for this PE block (for form element ids and keys). */
  instanceId?: string | number;
  /** Outcome ids to exclude from the PE dropdown (e.g. PEs already used in other blocks). */
  excludePeIds?: number[];
  onSave: (group: PerformanceExpectationGroup) => void;
  onCancel: () => void;
};

/**
 * Single PE block edit form: PE search + tag, DCI/SEP/CCC dimensions, Cancel and Save.
 * Used for both the first block and "Add another" PE.
 */
const PerformanceExpectationsEdit = ({
  subject,
  years,
  initialGroup,
  onSave,
  onCancel,
  isSaving = false,
  instanceId = 0,
  excludePeIds,
}: PerformanceExpectationsEditProps) => {
  const {
    formState,
    setFormState,
    questionTypes,
    setQuestionTypes,
    cerTypes,
    setCerTypes,
    outcomeOptions,
    outcomesLoading,
    setPerformanceExpectation,
    clearAll,
    performanceExpectationField,
  } = usePerformanceExpectationStandards({
    subject,
    years,
    initialFormState: initialGroup?.outcomes,
    initialQuestionTypes: initialGroup?.questionTypes,
    initialCerTypes: initialGroup?.cerTypes,
  });

  const peOptions = useMemo(() => {
    const options = outcomeOptions[performanceExpectationField] ?? [];
    if (!excludePeIds?.length) return options;
    const excludeSet = new Set(excludePeIds);
    return options.filter(opt => !excludeSet.has(opt.value));
  }, [outcomeOptions, performanceExpectationField, excludePeIds]);

  const peValues = formState[performanceExpectationField] ?? [];
  const hasPe = peValues.length > 0;

  const dimensionKeys = useMemo(() => {
    const fromOptions = Object.keys(outcomeOptions).filter(
      k => k !== performanceExpectationField
    );
    const fromFormState = Object.keys(formState).filter(
      k => k !== performanceExpectationField && (formState[k]?.length ?? 0) > 0
    );
    const uniqueKeys = [...new Set([...fromOptions, ...fromFormState])];
    return sortPerformanceExpectationDimensions(uniqueKeys);
  }, [outcomeOptions, performanceExpectationField, formState]);

  const allOtherOptions = useMemo(
    () =>
      flattenObjectToArrayWithKey(outcomeOptions).filter(
        o => o.key !== performanceExpectationField
      ),
    [outcomeOptions, performanceExpectationField]
  );

  const hasAnyOutcomes = useMemo(() => {
    if ((formState[performanceExpectationField] ?? []).length > 0) {
      return true;
    }
    return dimensionKeys.some(key => (formState[key] ?? []).length > 0);
  }, [formState, performanceExpectationField, dimensionKeys]);

  const handleSave = useCallback(() => {
    onSave({ outcomes: formState, questionTypes, cerTypes });
  }, [formState, questionTypes, cerTypes, onSave]);

  return (
    <SettingsGroup $marginBottom={0}>
      <div>
        <SettingsLabel>Performance Expectation</SettingsLabel>
        <TagAutocompleteOutcomes
          isMulti={false}
          values={peValues}
          isLoading={outcomesLoading}
          id={`key-activity-pe-${instanceId}`}
          placeholder="Search"
          isDisabled={hasPe}
          options={peOptions}
          allOtherOptions={allOtherOptions}
          handleChange={(value: OutcomeOption | OutcomeOption[] | null) => {
            const option = Array.isArray(value) ? value[0] : value;
            if (option) setPerformanceExpectation(option);
          }}
          optionsId={`${subject}-${years.join('_')}-${instanceId}`}
          className="key-activity-pe-search"
        />
        {hasPe && (
          <Box $marginTop={12}>
            <ButtonDismiss onClick={clearAll}>
              {peValues[0].label}
            </ButtonDismiss>
          </Box>
        )}
      </div>

      {outcomesLoading ? (
        <Bouncer />
      ) : (
        <>
          {hasPe &&
            dimensionKeys.map(key => {
              const options = outcomeOptions[key] ?? [];
              const values = formState[key] ?? [];
              const label = outcomeOptions[key]?.[0]?.category ?? key;

              return (
                <Box key={key} $marginTop={12}>
                  <SettingsLabel>{label}</SettingsLabel>
                  <TagAutocompleteOutcomes
                    isMulti
                    values={values}
                    isLoading={outcomesLoading}
                    id={`key-activity-${key}-${instanceId}`}
                    placeholder="Search"
                    options={options}
                    allOtherOptions={flattenObjectToArrayWithKey(
                      outcomeOptions
                    ).filter(o => o.key !== key)}
                    handleChange={(
                      value: OutcomeOption | OutcomeOption[] | null
                    ) => {
                      const nextValues = Array.isArray(value)
                        ? value
                        : value
                          ? [value]
                          : [];
                      setFormState(prev => ({
                        ...prev,
                        [key]: nextValues,
                      }));
                    }}
                    optionsId={`${subject}-${years.join(
                      '_'
                    )}-${key}-${instanceId}`}
                  />
                </Box>
              );
            })}
        </>
      )}

      {hasPe && (
        <>
          <SettingsGroup
            $marginTop={16}
            $marginBottom={0}
            data-testid={`key-activity-question-types-${instanceId}`}
          >
            <SettingsLabel>Question Types</SettingsLabel>
            <CheckableMulti
              name={`questionTypes-${instanceId}`}
              canDeselectAll
              options={QUESTION_TYPES}
              selected={questionTypes}
              onSelect={setQuestionTypes}
            />
          </SettingsGroup>
          <SettingsGroup
            $marginTop={16}
            $marginBottom={0}
            data-testid={`key-activity-cer-types-${instanceId}`}
          >
            <SettingsLabel>CER</SettingsLabel>
            <CheckableMulti
              name={`cerTypes-${instanceId}`}
              canDeselectAll
              options={CER_TYPE_OPTIONS}
              selected={cerTypes}
              onSelect={setCerTypes}
            />
          </SettingsGroup>
        </>
      )}

      <Box $justifyContent="space-between" $alignItems="center" $marginTop={24}>
        <LinkButton type="button" onClick={onCancel}>
          Cancel
        </LinkButton>
        <PrimaryButton
          type="button"
          $isSmall
          loading={isSaving}
          disabled={isSaving || !hasAnyOutcomes}
          onClick={handleSave}
        >
          Save
        </PrimaryButton>
      </Box>
    </SettingsGroup>
  );
};

export default PerformanceExpectationsEdit;
